import React, { useCallback, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useAuthContext } from 'AuthContext';
import MeetingHubProvider from './MeetingHubContext';
import MeetingStaffProvider from './MeetingStaffView/MeetingStaffContext';
import Spinner from 'components/shared/Spinner';
import SignInLink from 'components/shared/SignInLink';
import BackToTopButton from 'components/shared/BackToTopButton';
import GreetingSection from './GreetingSection';
import MeetingStaffView from './MeetingStaffView';
import MeetingMemberView from './MeetingMemberView';
import ActivityLogModal from './ActivityLogModal';
import ConnectionLogModal from './ConnectionLogModal';
import routes from 'store/configs/Routes';
import { MeetingView } from 'store/types/Meeting';
import useRequest from 'hooks/useRequest';
import MeetingService from 'services/MeetingService';
import { getFormattedDate } from 'util/format';
import { activityLogQueryParameter, previewQueryParameter } from 'util/meeting';

import styles from './MeetingPage.module.scss';

const MeetingPage: React.FunctionComponent = () => {
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const showPreview = searchParams.get(previewQueryParameter);
  const showActivityLog = searchParams.get(activityLogQueryParameter);
  const isPreview = showPreview === 'true';

  const { user, userLoading, profile, profileLoading, isStaff } = useAuthContext();
  const [activityLogModalOpen, setActivityLogModalOpen] = useState(false);
  const [connectionLogModalOpen, setConnectionLogModalOpen] = useState(false);

  const getMeetingRequest = useMemo(() => (!id ? undefined : () => MeetingService.getMeeting(id)), [id]);
  const { data: meeting, loading: meetingLoading } = useRequest<MeetingView>(getMeetingRequest);

  const loading = useMemo(
    () => userLoading || profileLoading || meetingLoading,
    [meetingLoading, profileLoading, userLoading]
  );

  const handleOpenActivityLogs = useCallback(() => {
    const { origin, pathname, hash } = window.location;

    const url = new URL(`${origin}${pathname}${hash}`);
    url.searchParams.set(activityLogQueryParameter, 'true');

    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  }, []);

  const mainContent = useMemo(
    () =>
      isStaff ? (
        <MeetingStaffProvider meetingId={id}>
          <MeetingStaffView />
        </MeetingStaffProvider>
      ) : (
        <MeetingMemberView />
      ),
    [id, isStaff]
  );

  return (
    <div id="meeting-page" className={`container ${loading ? 'emptySpacer' : ''} my-5`}>
      <Spinner loading={loading} transparent={false} className="h-100">
        {!user || !profile ? (
          <Alert severity="error" className="mb-3">
            Your session has expired or you are not logged in. Please <SignInLink>Sign In</SignInLink> and try again.
          </Alert>
        ) : !meeting ? (
          <Alert severity="error" className="mb-3">
            Invalid meeting. Please visit the <Link to={routes.meetings}>meetings</Link> page to find the correct
            meeting.
          </Alert>
        ) : isPreview || showActivityLog === 'true' ? (
          <MeetingHubProvider
            meetingId={id}
            attendeeId={profile.customer.internalID}
            isPreview={isPreview}
            isLog={showActivityLog === 'true'}
          >
            <Box className="my-3">
              <Typography className={styles.title} variant="h5">
                {meeting.title}
              </Typography>
              <Typography className={styles.subtitle} variant="subtitle1">
                {meeting.committees.join(', ')}
              </Typography>
            </Box>
            <Box className="mb-4">
              <Typography>Start Date: {getFormattedDate(meeting.startDate ?? '', 'L LT')}</Typography>
              <Typography>End Date: {getFormattedDate(meeting.endDate ?? '', 'L LT')}</Typography>
            </Box>
            {mainContent}
          </MeetingHubProvider>
        ) : (
          <MeetingHubProvider meetingId={id} attendeeId={profile.customer.internalID} isPreview={isPreview}>
            <Box display="flex" justifyContent={'space-between'}>
              <GreetingSection />
              <Box className={styles.buttonGroup}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setActivityLogModalOpen(true);
                  }}
                >
                  View Logs
                </Button>
                <Button variant="contained" color="primary" onClick={handleOpenActivityLogs}>
                  Pop-out Logs
                </Button>
                {isStaff && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setConnectionLogModalOpen(true);
                    }}
                  >
                    Connection Logs
                  </Button>
                )}
              </Box>
            </Box>
            <Box className="my-3">
              <Typography className={styles.title} variant="h5">
                {meeting.title}
              </Typography>
              <Typography className={styles.subtitle} variant="subtitle1">
                {meeting.committees.join(', ')}
              </Typography>
            </Box>
            <Box className="mb-4">
              <Typography>Start Date: {getFormattedDate(meeting.startDate ?? '', 'L LT')}</Typography>
              <Typography>End Date: {getFormattedDate(meeting.endDate ?? '', 'L LT')}</Typography>
            </Box>

            {mainContent}

            <ConnectionLogModal
              open={connectionLogModalOpen}
              onClose={() => {
                setConnectionLogModalOpen(false);
              }}
            />
            <ActivityLogModal
              open={activityLogModalOpen}
              onClose={() => {
                setActivityLogModalOpen(false);
              }}
            />
          </MeetingHubProvider>
        )}
        <BackToTopButton targetSelector="#meeting-page" />
      </Spinner>
    </div>
  );
};

export default MeetingPage;
